/**
 * Terrain generation framework
 * Generates chunk voxel data using layered noise functions with domain warping
 */
import { CHUNK_SIZE, GRID_SIZE, VOXEL_SCALE } from '../voxel/constants.js';
import { DEFAULT_PATHS_CONFIG, PATH_SKIN_MAX_STRIDE, PathNetwork } from './paths/PathNetwork.js';
import { surfaceBandFine } from '../voxel/surfaceBand.js';
import { mat } from '../materials/index.js';
import { packChunkKey, packPairKey } from '../util/packedKey.js';
import { DEFAULT_LANDFORM_CURVE } from './Curve.js';
import { LandformField } from './fields/LandformField.js';
import { resolveGenConfig } from './gen/resolveConfig.js';
/**
 * SEED BLOCKS. Every subsystem takes a fixed OFFSET from the world seed and consumes its block in
 * its own documented order, so adding a field to one cannot re-roll another's — which is what a
 * single `seed++` chain does, silently, to every world.
 *
 * The offsets are the ones the generator has always used, kept rather than tidied: changing one
 * changes every world at that seed for no benefit. `seedChain.test.ts` pins them.
 */
export const SEED_LANDFORM = 50000;
export const SEED_STAMPS = 10000;
import { ColumnProfile, solveWaterCrossing, PHASE_HEIGHT, PHASE_WATER, PHASE_EXTRUSION, PHASE_SKIN, } from './gen/ColumnProfile.js';
import { writeColumn, LANDFORM_SKIN_DEPTH, ROAD_SKIN_DEPTH } from './gen/ColumnPipeline.js';
import { NodeResolver, makeScatterKind, makeStampKind, scatterRoot, makeCityKind, makeBlockKind, makeBuildingKind, makeFloorKind, makeRoomKind, cityRoots, buildingTopAt, blockNode, buildingNode, } from './nodes/index.js';
import { MAX_BUILDING_LOD, blockIndex, cityGrid, gridU, gridV, RegionField, RegionType, DEFAULT_REGION_CONFIG, regionRoadMaterial, } from './regions/index.js';
import { StampPointGenerator, StampPlacer, DEFAULT_STAMP_DISTRIBUTION, getStampFootprintRadius, invalidateStampCache, setOakLargeShape, stampSpec, MAX_SCATTER_LOD, columnDot, } from './stamps/index.js';
// ============== Default Terrain Layer Configuration ==============
export { DEFAULT_LANDFORM_CURVE };
export const DEFAULT_TERRAIN_LAYER_CONFIG = {
    stampsEnabled: true, // trees / rocks / buildings on by default
    flatEnabled: false, // OFF: the default world is the landform one (see landformEnabled)
    groundLevel: 40, // voxels (~10 m) — the datum both height models stand on
    masterScale: 1, // world feature scale (1 = identity → existing worlds unchanged)
    landScale: 1, // land feature scale, on top of masterScale (1 = identity)
    // Landform layer — ON by default: this IS the game's terrain, and the only height model besides
    // `flat`. Note the reach of that: every server-side generator is built as
    // `new TerrainGenerator({ seed })` with no terrainLayer, so servers generate this world too.
    landformEnabled: true,
    seaCoveragePercent: 30, // ~30% ocean by default (percentile of the continental noise)
    landformScale: 1.0, // 1 = naturally-proportioned slopes (base λ derived from the relief)
    landformWarpScale: 1.0, // warp at the land frequency
    // Warp displacement in WAVELENGTHS. 0.03 (3% of a wavelength) was far too timid to see at all; the slider
    // now reaches 8, matching the reference's ~4 periods. 0.5 is the default because the effect changes
    // CHARACTER with magnitude, verified by rendering: up to ~0.5 it sweeps coastlines into sinuous bays and
    // headlands, and past ~1 it stops displacing and starts decorrelating — landmasses dissolve into thin
    // swirled ribbons. High values are the reference's territory and pair with landformRidged, not a default.
    landformWarpFraction: 0.5,
    landformMountainHeight: 4000, // voxels = 1 km peaks above sea; base λ derives to ~1.8 km landmasses
    landformSeaDepthFrac: 0.15, // ocean floor 15% of the relief below sea (~150 m at 1 km peaks)
    landformBeachWidthFrac: 0.005, // ~5 m of beach above the waterline at 1 km peaks
    // 0.5, not the 0.7 you might expect from "snow on the top 30%": the continental field is FBm, whose
    // values cluster, so the CONFIGURED mountain height is a ceiling the terrain approaches but rarely
    // reaches (measured max ≈ 0.79 × configured world-wide, and only ~0.70 × in a 4 km window). A snow line
    // at 0.7 therefore sits at or above the actual summits and snow vanishes entirely — which is exactly
    // what seaCoverage.test.ts's zone-visibility assertion caught. 0.5 ≈ 1.6% of columns under snow.
    landformSnowLineFrac: 0.5, // snow above ~500 m at 1 km peaks — visible caps on the real summits
    landformTerraceStepFrac: 0.02, // ~20 m cliff risers at 1 km peaks (0 = smooth)
    landformStepSharpness: 32, // reference value — near-vertical risers where the mask is strong
    landformStepCoverage: 0.25, // a quarter of the land stepped; the cubic bias keeps the rest smooth
    landformStepMix: 0.75, // reference is 0.5 (softer); 0.75 keeps the risers we already liked
    landformStepMaskScale: 16, // stepped districts at λ/16 (~220 m) — reference ratio
    landformStepWobble: 0.1, // reference value: up to +10% height, breaks the machined look
    landformStepWobbleScale: 64, // λ/64 (~56 m) — a couple of cycles per tread, so treads tilt/dome
    landformFlatnessAmount: 1.0, // full reference spread (exponent 0.6 → 2.0)
    landformFlatnessScale: 2, // λ/2 (~1.8 km) regions of mountain-vs-plain character
    landformRidged: 0, // 0 = keep the current FBm landforms; raise for alpine ridgelines
    landformOctaves: 10, // finest macro octave ~1.7 m at the default wavelength
    landformCurve: DEFAULT_LANDFORM_CURVE,
    landformDetailFrequency: 10, // ×0.03/m absolute → ~3 m base rubble (+ finer octaves)
    landformDetailFlat: 0.2, // slight texture on flat ground so plains aren't glassy
    // Detail amplitudes stay ABSOLUTE (voxels), not fractions of the relief: this is surface texture sized
    // against the player — climbing footholds and rubble. As a fraction it would put ~70 m of "rubble" on a
    // 1 km mountain (a second mountain range) and make plains bumpier every time you raised the peaks.
    // (Measured: this knob is nearly irrelevant to how rocky the world looks — 40 vs 6 moves the rock share
    // ~4% — so it stays at its tuned value rather than being scaled up with the relief.)
    landformDetailSteep: 20, // ~5 m of rubble at vertical, ramped in (squared) by slope
    landformRockSlopeDeg: 30, // rock shows from ~30° — an ANGLE, so it needs no scaling at all
    // The skin, at exactly the ids the literals in LandformField resolved to, so the merge moves nothing.
    surfaceGroundMat: mat('moss2'),
    surfaceBeachMat: mat('sand'),
    surfaceSeabedMat: mat('gravel'),
    surfaceSteepMat: mat('rock'),
    surfaceSnowMat: mat('snow'),
    waterMaterial: mat('water'),
};
// ============== Default Configuration ==
export const DEFAULT_TERRAIN_CONFIG = {
    seed: 12345,
    materialLayers: [
        { materialId: mat('moss2'), maxDepth: 2 }, // Grass: 0-2 voxels deep
        { materialId: mat('rock'), maxDepth: 8 }, // Rock: 2-8 voxels deep
        { materialId: mat('rock2'), maxDepth: Infinity }, // Stone: 8+ voxels
    ],
    defaultMaterial: mat('rock2'),
    enableStamps: true,
    regionConfig: DEFAULT_REGION_CONFIG,
    terrainLayer: DEFAULT_TERRAIN_LAYER_CONFIG,
};
// ============== Terrain Generator ==============
/** Stand-in for the voxel array on a walk that only MEASURES (the column clip). Never written. */
const EMPTY_SINK_DATA = new Uint32Array(0);
export class TerrainGenerator {
    config;
    /** The landform height model — sea, beaches, plains, mountains — and everything from that shape. */
    landform;
    cityDeps;
    /** Side channel from cityMassTop: the block's body material, so the coarse mass is the same colour
     *  the building would have been. Read immediately by the one caller, like terraceGain. */
    cityMassMat = 0;
    /** Side channel from regionWallTop: signed fine-voxel distance to the wall, negative INSIDE it. */
    wallDistFine = Infinity;
    /** ...and from cityMassTop: the same, to the city mass's outer face. */
    cityMassDistFine = Infinity;
    /** Block nodes per city cell, and building nodes per block. Both are pure functions of position, so
     *  they outlive the chunk request that built them — see the note at the registration site. */
    cityBlockCache = new Map();
    /** NESTED BY CITY, not a single map under a combined key. A city id is a packPairKey — up to 2^34 —
     *  so any bitwise combination of it with a block key truncates to 32 bits and collides: measured,
     *  15212 of 16224 blocks shared a key with a different block, and because most blocks legitimately
     *  have no building the poison was overwhelmingly a cached `null`. Towers vanished at whichever LOD
     *  happened to ask second. */
    cityBuildingCache = new Map();
    // Per-column 2D surface height, memoized so a vertical stack of chunks (same cx,cz, different cy)
    // doesn't recompute the ~9-noise height field per cy. Keyed by EXACT world (x,z): grid queries across
    // cy use identical floats so they hit; off-grid callers (spawn/collision) miss and recompute exactly,
    // so results are unchanged. Cleared on any config/seed change (invalidateCaveCaches).
    heightCache = new Map();
    heightCacheEntries = 0;
    // Per-(cx,cz) FILTERED stamp placements, memoized so the 25-neighbour scatter + O(n²) collision +
    // pathway/breach filter isn't recomputed for every cy in a column (the result is XZ-only). Bounded;
    // cleared on config/seed change.
    placementCache = new Map();
    // Same idea for COARSE chunks, but keyed by (cx,cz,LOD): coarse coords live in their own grid per
    // level, so a lod-less key would hand a lod-2 column a lod-1 list. Kept separate from
    // placementCache rather than sharing it — that map's keys are lod-0 fine-chunk coords and the LOD-0
    // byte-identity guards depend on nothing else writing into it.
    lodPlacementCache = new Map();
    // Stamp system
    stampPointGenerator = null;
    stampPlacer = null;
    /**
     * THE NODE WALK. Every piece of hierarchical content a chunk contains is emitted through this —
     * today only the scatter, which is the point of doing it before there is anything else
     * (docs/hierarchical-region-system.md M3a). Built once; the kinds close over this generator's
     * placement pass, placer and height sampler.
     */
    nodes = new NodeResolver();
    /** The region field, or null when regions are off — so a regions-off world constructs nothing and
     *  runs exactly the code it ran before. */
    regionField = null;
    /**
     * Build from the AUTHORED config — the node tree a world stores and the editor edits. The one
     * public door for anything that owns a world; the constructor's resolved shape is for guards and
     * defaults.
     */
    static fromGenConfig(cfg) {
        return new TerrainGenerator(resolveGenConfig(cfg));
    }
    /** The stamp distributions as the CONFIG asked for them, before masterScale is folded in. Held so
     *  applyTerrainLayer can be re-run without compounding, and without reverting to the defaults. */
    authoredDistributions = DEFAULT_STAMP_DISTRIBUTION.distributions;
    constructor(config = {}) {
        this.config = { ...DEFAULT_TERRAIN_CONFIG, ...config };
        if (config.stampConfig?.distributions) {
            this.authoredDistributions = config.stampConfig.distributions;
        }
        if (config.materialLayers) {
            this.config.materialLayers = config.materialLayers;
        }
        if (config.terrainLayer) {
            this.config.terrainLayer = { ...DEFAULT_TERRAIN_LAYER_CONFIG, ...config.terrainLayer };
        }
        // Fold the friendly Terrain-layer tunables onto the stamp distribution (single source of truth).
        this.applyTerrainLayer();
        this.applyOakShape();
        this.landform = new LandformField(this.config.terrainLayer, this.config.seed + SEED_LANDFORM, this.landSizeScale(), (x, z) => this.sampleHeight(x, z));
        // Initialize stamp system if enabled
        if (this.config.enableStamps) {
            this.initializeStampSystem();
        }
        this.rebuildRegionField();
        this.rebuildPathNetwork();
        this.refreshWriteContext();
        // The node kinds. `scatter` owns the chunk-footprint enumeration that already existed; `stamp`
        // is its leaf and does the drawing.
        const scatterDeps = {
            placements: (cx, cz, lod) => this.scatterPlacements(cx, cz, lod),
            placer: () => this.stampPlacer,
            heights: this,
        };
        this.nodes.register(makeScatterKind(scatterDeps));
        this.nodes.register(makeStampKind(scatterDeps));
        // `city -> block -> building`. Enumeration is CACHED PER CITY, not per chunk: a cell holds ~25
        // blocks at the defaults and none of them depends on the request, while `resolveNodes` runs once
        // per chunk INCLUDING every cy in a column. Without this a 30-chunk column would re-seat every
        // tower thirty times.
        this.cityDeps = {
            cfg: () => this.config.regionConfig,
            cities: (w) => this.citiesIn(w),
            blocksOf: (city) => this.blocksOf(city),
            buildingOf: (block) => this.buildingOf(block),
        };
        this.nodes.register(makeCityKind(this.cityDeps));
        this.nodes.register(makeBlockKind(this.cityDeps));
        this.nodes.register(makeBuildingKind());
        this.nodes.register(makeFloorKind());
        this.nodes.register(makeRoomKind());
    }
    /**
     * The CITY cells whose territory can reach a chunk window. One hash per lattice cell tested, so the
     * ~16 cells a lod-5 window covers cost nothing; the cells that survive are memoised as nodes.
     */
    citiesIn(w) {
        const rf = this.regionField;
        if (!rf || !this.config.regionConfig.buildingsEnabled)
            return [];
        const out = [];
        rf.forEachCell(w.minX * VOXEL_SCALE, w.minZ * VOXEL_SCALE, w.maxX * VOXEL_SCALE, w.maxZ * VOXEL_SCALE, (ci, cj) => {
            if (rf.typeAt(ci, cj) !== RegionType.CITY)
                return;
            const c = rf.cellCentre(ci, cj);
            out.push({ id: packPairKey(ci, cj), ci, cj, centreX: c.x, centreZ: c.z });
        });
        return out;
    }
    /** Block nodes of one city, memoised by cell id. */
    blocksOf(city) {
        let blocks = this.cityBlockCache.get(city.id);
        if (blocks)
            return blocks;
        const cfg = this.config.regionConfig;
        // The blocks a cell's territory can hold: its lattice square grown by the feature point's reach,
        // in the grid's own frame. Generous — a block outside the territory is rejected by the building.
        const reach = cfg.cellSize * (0.5 + Math.min(0.5, cfg.jitter));
        const n = Math.ceil(reach / Math.max(1, cfg.roadSpacing)) + 1;
        blocks = [];
        for (let bj = -n; bj <= n; bj++) {
            for (let bi = -n; bi <= n; bi++) {
                const node = blockNode({ city, bi, bj }, cfg);
                if (node)
                    blocks.push(node);
            }
        }
        if (this.cityBlockCache.size > 256)
            this.cityBlockCache.clear();
        this.cityBlockCache.set(city.id, blocks);
        return blocks;
    }
    /** The building of a block, memoised. Null is cached too — most rejections are near a boundary. */
    buildingOf(block) {
        let per = this.cityBuildingCache.get(block.city.id);
        if (!per) {
            if (this.cityBuildingCache.size > 256)
                this.cityBuildingCache.clear();
            per = new Map();
            this.cityBuildingCache.set(block.city.id, per);
        }
        const key = packPairKey(block.bi, block.bj);
        const hit = per.get(key);
        if (hit !== undefined)
            return hit;
        const node = buildingNode(block, this.config.regionConfig, this.nodeContext(), (x, z) => this.rejectBuilding(block.city, x, z), (x, z, clearance) => this.regionField.wallOverlaps(x, z, clearance));
        per.set(key, node);
        return node;
    }
    /**
     * May a building stand at this lot CENTRE? The two questions that are about where the lot is; the
     * wall — which is about how far the lot reaches — is asked per tap inside buildingNode.
     */
    rejectBuilding(city, x, z) {
        const rf = this.regionField;
        // TERRITORY, NOT LATTICE. A block near the boundary can fall in the NEIGHBOUR's ground, and both
        // cities would otherwise build there on two different grids.
        const s = rf.sample(x, z);
        if (s.type !== RegionType.CITY || packPairKey(s.ci, s.cj) !== city.id)
            return true;
        // Nothing below the beach line: a skyline standing in the sea is the first thing you would see
        // the moment landform comes back on.
        return this.landform.suppressed(x, z);
    }
    /** GenContext with the current world scale — read per request, since masterScale is configurable. */
    nodeContext() {
        return {
            height: (x, z) => this.sampleHeight(x, z),
            scale: this.masterScale,
            interiors: !!this.config.regionConfig.interiorsEnabled,
        };
    }
    /** The chunk window a walk is clipped to: the chunk's own fine-voxel box grown by the meshed
     *  footprint the mesher borrows (two columns past each + face), so a node standing in the borrowed
     *  band — drawn by THIS chunk and by nobody else — is not pruned away. */
    chunkWindow(cx, cy, cz, lod) {
        const stride = 1 << lod;
        const span = CHUNK_SIZE * stride;
        const margin = Math.max(16, 2 * stride);
        return {
            minX: cx * span - margin, maxX: (cx + 1) * span + margin,
            minY: cy * span - margin, maxY: (cy + 1) * span + margin,
            minZ: cz * span - margin, maxZ: (cz + 1) * span + margin,
        };
    }
    /**
     * The filtered scatter placements for a chunk footprint at a level — LOD 0 and coarse have always
     * been two different passes (chunk-local vs footprint-walked), and this is the one place that
     * difference lives now.
     */
    scatterPlacements(cx, cz, lod) {
        if (!this.config.terrainLayer.stampsEnabled || !this.stampPointGenerator || !this.stampPlacer) {
            return [];
        }
        if (lod > 0)
            return this.lodPlacements(cx, cz, lod);
        const pkey = packPairKey(cx, cz);
        let placements = this.placementCache.get(pkey);
        if (!placements) {
            placements = this.stampPointGenerator.generateForChunk(cx, cz).filter((p) => this.keepStamp(p, 0));
            if (this.placementCache.size > 4096)
                this.placementCache.clear();
            this.placementCache.set(pkey, placements);
        }
        return placements;
    }
    /** Run the node walk for one chunk. */
    resolveNodes(data, cx, cy, cz, lod) {
        const sink = {
            cx, cy, cz, lod, data, window: this.chunkWindow(cx, cy, cz, lod),
        };
        this.nodes.resolve([scatterRoot(sink), ...cityRoots(sink, this.cityDeps)], sink, this.nodeContext());
    }
    /** (Re)build the region field from config — null when regions are off. */
    rebuildRegionField() {
        const rc = this.config.regionConfig;
        this.regionField = rc?.enabled ? new RegionField(this.config.seed, rc) : null;
        this.cityBlockCache.clear();
        this.cityBuildingCache.clear();
    }
    /**
     * Initialize the stamp generation system
     */
    initializeStampSystem() {
        const stampConfig = {
            seed: this.config.seed + SEED_STAMPS,
            ...this.config.stampConfig,
        };
        this.stampPointGenerator = new StampPointGenerator(stampConfig);
        this.stampPlacer = new StampPlacer();
    }
    /**
     * Update the generator configuration
     */
    setConfig(config) {
        this.config = { ...this.config, ...config };
        if (config.materialLayers) {
            this.config.materialLayers = config.materialLayers;
        }
        if (config.stampConfig?.distributions) {
            this.authoredDistributions = config.stampConfig.distributions;
            this.applyTerrainLayer();
            this.initializeStampSystem();
        }
        if (config.terrainLayer) {
            this.config.terrainLayer = { ...this.config.terrainLayer, ...config.terrainLayer };
            this.applyTerrainLayer();
            this.landform.setConfig(this.config.terrainLayer, this.landSizeScale());
            this.refreshWriteContext();
            this.invalidateColumnCaches(); // landform reshapes height → drop the per-column memos
        }
        if (config.regionConfig) {
            this.config.regionConfig = { ...this.config.regionConfig, ...config.regionConfig };
            // Rebuilt rather than mutated: the field bakes its noise frequencies and lattice salts at
            // construction, and a half-updated field is a diagram that disagrees with itself across a chunk
            // boundary. Its per-column memo goes with it, which is the point.
            this.rebuildRegionField();
        }
        if (config.oakLarge) {
            this.applyOakShape();
        }
        if (config.seed !== undefined) {
            this.landform.setSeed(config.seed + SEED_LANDFORM);
            this.rebuildRegionField(); // the field's salts and warp seeds derive from config.seed
            this.invalidateColumnCaches();
        }
        // THE PATHS ARE DOWNSTREAM OF EVERYTHING: they are traced over the heights, between the region
        // nodes, so any change to terrain, regions, seed or their own knobs rebuilds them — and drops
        // the height memos and placement memos, because the BENCH the paths cut is part of the height
        // and the scatter keeps off the ribbon.
        if (config.terrainLayer || config.regionConfig || config.seed !== undefined || config.pathsConfig) {
            this.rebuildPathNetwork();
            this.invalidateColumnCaches();
            this.placementCache.clear();
            this.lodPlacementCache.clear();
        }
    }
    /**
     * The region-to-region paths, or null (paths off, or no region field to hang them on). Rebuilt
     * whole on any config change that moves terrain, regions or the paths themselves: its traces are
     * memoised against the heights and node positions it was built over.
     */
    pathNetwork = null;
    rebuildPathNetwork() {
        const pc = { ...DEFAULT_PATHS_CONFIG, ...this.config.pathsConfig };
        const rf = this.regionField;
        if (!rf || !pc.enabled) {
            this.pathNetwork = null;
            return;
        }
        const tl = this.config.terrainLayer;
        this.pathNetwork = new PathNetwork(pc, {
            rawHeight: (x, z) => this.baseHeight(x, z),
            // Routing reads the SMOOTH macro field (see PathNetworkDeps.routeHeight): right for a
            // macroscopic router, and a third of the noise. Without landform there is nothing to smooth.
            routeHeight: tl.landformEnabled && !tl.flatEnabled
                ? (x, z) => this.landform.macroSmooth(x, z)
                : (x, z) => this.baseHeight(x, z),
            cellCentre: (ci, cj) => rf.cellCentre(ci, cj),
            cellSize: rf.getConfig().cellSize,
            seaFine: tl.landformEnabled ? tl.groundLevel : -Infinity,
            seed: this.config.seed,
        });
    }
    /**
     * A spawn point on the path network — the nearest region node that stands on land.
     *
     * NO TRACING, AND THAT IS THE WHOLE POINT. Every path of a region ends at its node (the junction
     * guarantee pathNetwork.test asserts), so a node IS the network's position, known from arithmetic
     * alone: `cellCentre` is a hash and a warp, no A*, no height field beyond one sample per
     * candidate. The earlier version traced pairs and ring-probed to land within 0.75 m of a
     * centreline, which cost a cold sweep and two special-case PathNetwork entry points to keep
     * bounded; "near a path" is all a spawn needs, and the node is within a lattice step of every
     * path that ends there.
     *
     * Null when regions or paths are off. Returns world metres; `y` is the raw ground (the bench
     * moves it by at most the path's own profile, which the spawn probe re-reads from the field).
     */
    findPathSpawn() {
        const rf = this.regionField;
        if (!this.pathNetwork || !rf)
            return null;
        const tl = this.config.terrainLayer;
        const sea = tl.landformEnabled ? tl.groundLevel : -Infinity;
        const nodes = [];
        for (let cj = -2; cj <= 2; cj++) {
            for (let ci = -2; ci <= 2; ci++) {
                const c = rf.cellCentre(ci, cj);
                nodes.push({ x: c.x, z: c.z, d: c.x * c.x + c.z * c.z });
            }
        }
        nodes.sort((a, b) => a.d - b.d);
        for (const n of nodes) {
            // Clear of the water by the router's own shore margin, or the node's pairs never traced and
            // the spawn would stand at a junction of nothing.
            const h = this.baseHeight(n.x, n.z);
            if (h <= sea + 8)
                continue;
            return { x: n.x, y: h * VOXEL_SCALE, z: n.z };
        }
        return null;
    }
    /**
     * Push the config's oak shape into the spec. When it CHANGED, the cached models and this
     * generator's placement memos are stale — the model's bounds moved, and the scatter filters on
     * them — so both are dropped. A no-op when the shape is the one already installed, which is every
     * generator after the first in a process (they all serve the same world).
     */
    applyOakShape() {
        if (!this.config.oakLarge)
            return;
        if (setOakLargeShape(this.config.oakLarge)) {
            invalidateStampCache();
            this.placementCache.clear();
            this.lodPlacementCache.clear();
        }
    }
    /** Drop every per-column memo (after a config or seed change). */
    invalidateColumnCaches() {
        this.heightCache.clear();
        this.heightCacheEntries = 0;
        this.landform.clearCaches();
        this.placementCache.clear();
        this.lodPlacementCache.clear();
        this.cityBlockCache.clear();
        this.cityBuildingCache.clear();
    }
    /**
     * Fold the friendly Terrain-layer tunables onto the stamp distribution, which is where the
     * generator actually reads them.
     *
     * IDEMPOTENT, AND IT DERIVES FROM THE AUTHORED LIST rather than from the last result, so
     * re-applying never compounds. `authoredDistributions` is captured once, at construction, which is
     * the fix for a real defect: this used to derive from DEFAULT_STAMP_DISTRIBUTION unconditionally,
     * so a caller's own distributions were overwritten the moment the config was applied — every
     * per-stamp spacing, jitter and clearance in the world dialog was inert, and so was every stamp's
     * tickbox.
     */
    applyTerrainLayer() {
        const t = this.config.terrainLayer;
        // FLAT WINS, AND IT IS SETTLED HERE, ONCE. Every generation path reads `landformEnabled`
        // directly — a dozen sites — so expressing "flat overrides it" as a derived boolean would mean a
        // dozen chances to read the raw flag and disagree. Normalising the working config instead makes
        // every existing site correct with no edit.
        if (t.flatEnabled) {
            t.landformEnabled = false;
        }
        const m = t.masterScale > 0 ? t.masterScale : 1; // world scale: bigger = sparser/larger, so ×m spacing
        // Every distribution scales with masterScale so density-per-area holds as the world stretches.
        // At masterScale = 1 the authored list passes through untouched.
        const distributions = m === 1 ? [...this.authoredDistributions]
            : this.authoredDistributions.map((d) => ({ ...d, gridSize: d.gridSize * m, exclusionRadius: d.exclusionRadius * m }));
        this.config.stampConfig = { ...this.config.stampConfig, distributions };
    }
    /** World scale (masterScale), clamped > 0. */
    get masterScale() {
        const m = this.config.terrainLayer.masterScale;
        return m > 0 ? m : 1;
    }
    /** Combined world+land size divisor: bigger master/land scale → lower frequency → larger features. */
    landSizeScale() {
        const t = this.config.terrainLayer;
        const m = t.masterScale > 0 ? t.masterScale : 1;
        const l = t.landScale > 0 ? t.landScale : 1;
        return m * l;
    }
    /** True if the column containing (worldX, worldZ) is a cave breach. Resolves the tile that OWNS that
     *  column and consults ITS breach set, so the answer is a pure function of world position — identical
     *  from whichever chunk renders the stamp. A stamp anchored in one tile but overlapping into its
     *  neighbours is therefore suppressed (or kept) as a whole, not half-drawn across the boundary. */
    /**
     * Get the current configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Sample terrain height at a world position
     * @param worldX - World X coordinate in meters
     * @param worldZ - World Z coordinate in meters
     * @returns Height in voxels
     */
    /** The terrain's own height, before the path bench — what the paths are traced OVER. */
    baseHeight(worldX, worldZ) {
        const t = this.config.terrainLayer;
        return t.flatEnabled ? t.groundLevel
            : t.landformEnabled ? this.landform.height(worldX, worldZ)
                : 0;
    }
    sampleHeight(worldX, worldZ) {
        // Memo: a vertical stack of chunks re-queries the same (worldX,worldZ) grid per cy. Exact-float
        // key, so off-grid callers just miss (recompute exactly) — output unchanged.
        let inner = this.heightCache.get(worldX);
        if (inner) {
            const cached = inner.get(worldZ);
            if (cached !== undefined)
                return cached;
        }
        // TWO HEIGHT MODELS, and flat wins (applyTerrainLayer has already forced landform off beside it).
        // A world with neither is flat at 0 — there is no third model to fall through to.
        const base = this.baseHeight(worldX, worldZ);
        // THE PATH BENCH lives inside sampleHeight so that every reader of heights — chunks at every
        // LOD, stamp seating, physics, the vertical clip — agrees about it by construction. The blend's
        // own broad-phase makes the common case (no path near) one grid read.
        const height = this.pathNetwork ? this.pathNetwork.blendHeight(worldX, worldZ, base) : base;
        // Bounded: clear wholesale past a generous cap (covers the active view's columns) rather than LRU.
        if (this.heightCacheEntries > 262144) {
            this.heightCache.clear();
            this.heightCacheEntries = 0;
            inner = undefined;
        }
        if (!inner) {
            inner = new Map();
            this.heightCache.set(worldX, inner);
        }
        inner.set(worldZ, height);
        this.heightCacheEntries++;
        return height;
    }
    /**
     * Sample terrain surface at a world position (height + material)
     * Used for map tile generation without creating full chunks.
     * @param worldX - World X coordinate in meters
     * @param worldZ - World Z coordinate in meters
     * @returns Surface height (voxels) and material ID
     */
    sampleSurface(worldX, worldZ) {
        // THE MAP TILE READS THE COLUMN THE DRAW WRITES. This used to restate the height model, the sea,
        // the road and the wall in its own words — a fourth copy of the same predicates — and the copy
        // drifted: it reported bare ground under a city, which capped every LOD-0 column four chunks
        // above the terrain and sliced 22% of buildings off at 39.8 m (26.2 m lost on average, 112.5 at
        // worst), with the tile then reporting the stump's height so nothing above was ever requested.
        const p = this.resolveColumn(worldX, worldZ, 1);
        const tl = this.config.terrainLayer;
        // Height stays at the TERRAIN floor: liquid and transparent fills sit above it but must not
        // raise the reported height, or getChunkRangeFromHeights skips the chunk holding the solid
        // surface. Submerged columns read as water on the 2D map.
        let height = p.height;
        let material = tl.landformEnabled
            ? (p.water > -Infinity ? p.waterMat : p.surfaceMat)
            : this.getMaterialAtDepth(0);
        if (p.onRoad)
            material = this.writeCtx.roadMaterial;
        // The boundary wall is content standing ON the column, so the tile reports its top and its
        // colour — the region diagram is exactly the thing a map should show. `wallDistFine < 0` is the
        // wall ITSELF; regionWallTop also answers for the band column beside it, which is air and must
        // not raise the reported height. (That trap put a spawn marker in mid-air once.)
        if (this.regionField) {
            const wallTop = this.regionWallTop(worldX, worldZ, height, 1);
            if (this.wallDistFine < 0 && wallTop > height) {
                height = wallTop;
                material = this.regionWallMaterialAt(worldX, worldZ);
            }
            // A TOWER IS CONTENT ON THE COLUMN, exactly as the wall is, and this is not only about the
            // map: the LOD-0 streamer finds a column's vertical extent by scanning upward from what THIS
            // function reports (LocalTerrainSource.buildSurfaceColumn, four chunks above). The margin was
            // sized for tree canopies, and it means what it says again once the ceiling is the building
            // rather than the ground.
            const b = this.cityBuildingAt(worldX, worldZ);
            if (b) {
                const top = buildingTopAt(b, worldX, worldZ);
                if (top > height) {
                    height = top;
                    material = b.roof;
                }
            }
        }
        return { height: Math.floor(height), material };
    }
    /**
     * Get material ID based on depth from surface
     * @param depthFromSurface - Depth in voxels (positive = inside terrain)
     * @returns Material ID
     */
    getMaterialAtDepth(depthFromSurface) {
        for (const layer of this.config.materialLayers) {
            if (depthFromSurface <= layer.maxDepth) {
                return layer.materialId;
            }
        }
        return this.config.defaultMaterial;
    }
    // ============== Regions ==============
    /**
     * Fine-voxel Y that the region BOUNDARY WALL reaches in this column, or -Infinity where there is no
     * wall. The one place the wall's extent is decided, so the three callers that must agree about it —
     * generateChunk, generateChunkLOD and the coarse column CLIP — cannot drift.
     *
     * `strideFine` is the fine voxels one sample stands for (1 at LOD 0, 1 << lod above it). It is not a
     * detail: the coarse paths sample ONE point per coarse column, so the wall predicate has to be the
     * MAX over the fine columns that point stands for, or a 4 m wall inside an 8 m lod-5 column registers
     * only where the sample happens to land in the band and the wall comes out DASHED. RegionField
     * widens the `edge` threshold by the cell's reach to do that max analytically, in one evaluation
     * rather than (2^lod)^2 of them — see RegionField.wallEdgeThreshold.
     */
    regionWallTop(worldX, worldZ, terrainHeight, strideFine) {
        const rf = this.regionField;
        this.wallDistFine = Infinity;
        if (!rf)
            return -Infinity;
        // THE CELL, NOT THE POINT. A coarse sample stands for the whole block of fine columns it covers,
        // so the wall question is asked of that block (RegionField.wallCellDistance does the max over
        // it). The sample sits at the block's low CORNER, hence the centre offset; half-extent is zero at
        // LOD 0, where the sample IS the voxel and there is nothing to downsample.
        const half = (strideFine - 1) * VOXEL_SCALE * 0.5;
        const d = rf.wallCellDistance(worldX + half, worldZ + half, half, strideFine) / VOXEL_SCALE;
        this.wallDistFine = d;
        // A TOP FOR THE BAND COLUMNS TOO, one coarse voxel wider than the wall itself. Those columns
        // carry the NEGATIVE half of the surface band — air, but air that is nearer the wall than a
        // saturated -0.5, which is the whole of what makes the wall's face smooth. A column skipped here
        // is a column the air-column early-out drops and the coarse clip never creates, and the smoothing
        // would then stop dead at whichever chunk boundary the wall happens to leave.
        //
        // ANYTHING THAT WANTS "IS THE WALL ACTUALLY HERE" MUST READ `wallDistFine < 0`, not this. That is
        // the same trap `surfaceHeightAt` fell into over a city: a height reported for a column the thing
        // does not stand on puts the spawn marker in the air.
        if (!(d < surfaceBandFine(strideFine)))
            return -Infinity;
        return terrainHeight + Math.max(rf.getConfig().wallHeight, strideFine);
    }
    /** Wall material for the column — the NEAREST region's, so a boundary is each side's own colour
     *  meeting in the middle and needs no ownership rule to decide between them. */
    /**
     * Is the city road grid on this column? `strideFine` is the fine voxels one sample stands for, as
     * for regionWallTop — but the downsample rule is the opposite one (COVERAGE, not max: see
     * CityLayout), because over-drawing a line is right for a wall and paves a city over.
     *
     * Material only, deliberately: a road that also cut the terrain would put the region system into
     * `generateLodColumn`'s vertical clip, and nothing about M3b needs that. Roads-as-a-dip is a later
     * change and it is the clip that makes it one.
     */
    /**
     * The building standing over a world position, or null. Resolved through the same block/building
     * caches the node walk uses, so asking is a hash and a Map probe after the first time.
     */
    cityBuildingAt(worldX, worldZ) {
        const rf = this.regionField;
        const cfg = this.config.regionConfig;
        if (!rf || !cfg.buildingsEnabled)
            return null;
        const s = rf.sample(worldX, worldZ);
        if (s.type !== RegionType.CITY)
            return null;
        const g = cityGrid(s.id, cfg.roadSpacing);
        const city = { id: s.id, ci: s.ci, cj: s.cj, centreX: s.centreX, centreZ: s.centreZ };
        const bi = blockIndex(gridU(worldX, worldZ, s.centreX, s.centreZ, g), cfg.roadSpacing);
        const bj = blockIndex(gridV(worldX, worldZ, s.centreX, s.centreZ, g), cfg.roadSpacing);
        const node = this.buildingOf({ city, bi, bj });
        return node ? node.data : null;
    }
    /**
     * THE CITY ABOVE MAX_BUILDING_LOD, per column — fine-voxel Y the block silhouette reaches, or
     * -Infinity where there is none. The same trade `columnDot` makes for trees: past the level where
     * enumerating each one pays for itself, draw the statistic instead.
     *
     * AND THE STATISTIC IS THE REAL THING, not noise. `blockProfileHeight` is what the block's building
     * is built up to, so crossing the cutoff changes the FOOTPRINT — a filled block becomes a tiered
     * tower — and never the height. A noise stand-in would give a skyline that rises and falls as you
     * fly in, which is exactly what the dot's shared height exists to avoid for trees.
     *
     * Roads are not drawn at these levels (roadDrawable says a 6 m street is not expressible on a 16 m
     * lattice), so the mass merges across them. Blocks are 40 m against a 16 m cell, so block-to-block
     * height variation survives, which is what makes it still read as a city.
     */
    cityMassTop(worldX, worldZ, terrainHeight, strideFine) {
        const rf = this.regionField;
        const cfg = this.config.regionConfig;
        this.cityMassDistFine = Infinity;
        if (!rf || !cfg.buildingsEnabled)
            return -Infinity;
        const half = (strideFine - 1) * VOXEL_SCALE * 0.5;
        const x = worldX + half, z = worldZ + half;
        const s = rf.sample(x, z);
        if (s.type !== RegionType.CITY)
            return -Infinity;
        // THE CITY'S OUTER FACE IS THE ONE THAT IS VISIBLE, and its distance is already in hand: the mass
        // stops at the inner edge of the wall's band, so `dist - halfWidth` measures straight to it. That
        // is the silhouette you see a city by from range, and it gets a band like every other face
        // (voxel/surfaceBand.ts). The other edges here — landform suppression, and a block whose profile
        // is zero — stay hard: they are decisions, not surfaces, and at these levels a block is under
        // three voxels across, so there is nothing to interpolate between them anyway.
        const edgeD = (cfg.wallWidth * 0.5 - s.dist) / VOXEL_SCALE;
        this.cityMassDistFine = edgeD;
        if (!(edgeD < surfaceBandFine(strideFine)))
            return -Infinity;
        if (this.landform.suppressed(x, z))
            return -Infinity;
        // THE MASS READS THE SAME MEMOISED BUILDING THE NODE WALK DOES — it used to re-derive
        // blockProfileHeight privately, with none of buildingNode's gates, so a lot the relief guard
        // rejected at LOD 0-5 still had a full silhouette at range that evaporated on approach. One
        // author for "does a building stand here, how tall, what colour" makes that disagreement
        // unwritable, and the block's seat survey is paid once and shared with the close-up levels.
        const g = cityGrid(s.id, cfg.roadSpacing);
        const bi = blockIndex(gridU(x, z, s.centreX, s.centreZ, g), cfg.roadSpacing);
        const bj = blockIndex(gridV(x, z, s.centreX, s.centreZ, g), cfg.roadSpacing);
        const city = { id: s.id, ci: s.ci, cj: s.cj, centreX: s.centreX, centreZ: s.centreZ };
        const node = this.buildingOf({ city, bi, bj });
        if (!node)
            return -Infinity;
        const b = node.data;
        this.cityMassMat = b.body;
        // The building's OWN top — the real skyline, not terrain-following — floored at one coarse
        // voxel above the column's ground like the wall and the dot, so the mass never vanishes into
        // a row remainder.
        return Math.max(b.seatY + b.height, terrainHeight + strideFine);
    }
    regionRoadAt(worldX, worldZ, strideFine) {
        const rf = this.regionField;
        if (!rf)
            return false;
        const half = (strideFine - 1) * VOXEL_SCALE * 0.5;
        const x = worldX + half, z = worldZ + half;
        if (!rf.isRoadCell(x, z, half))
            return false;
        // A ROAD SERVES A DOORSTEP: paving only where at least one flanking block holds a building,
        // so the grid doesn't run through empty outskirts. The four candidates (the position pushed a
        // road's width each way in the grid frame) cover a straight edge, a corner and an intersection
        // alike; buildingOf is memoised, so past a block's first touch this is Map probes. With
        // buildings off entirely the old behaviour stands — a bare grid is then the feature itself.
        const cfg = this.config.regionConfig;
        if (!cfg.buildingsEnabled)
            return true;
        const s = rf.sample(x, z);
        const g = cityGrid(s.id, cfg.roadSpacing);
        const u = gridU(x, z, s.centreX, s.centreZ, g);
        const v = gridV(x, z, s.centreX, s.centreZ, g);
        const city = { id: s.id, ci: s.ci, cj: s.cj, centreX: s.centreX, centreZ: s.centreZ };
        for (const du of [-cfg.roadWidth, cfg.roadWidth]) {
            for (const dv of [-cfg.roadWidth, cfg.roadWidth]) {
                if (this.buildingOf({
                    city,
                    bi: blockIndex(u + du, cfg.roadSpacing),
                    bj: blockIndex(v + dv, cfg.roadSpacing),
                }))
                    return true;
            }
        }
        return false;
    }
    regionWallMaterialAt(worldX, worldZ) {
        return this.regionField.wallMaterial(this.regionField.sample(worldX, worldZ).type);
    }
    /**
     * Generate voxel data for a chunk
     * @param cx - Chunk X coordinate
     * @param cy - Chunk Y coordinate
     * @param cz - Chunk Z coordinate
     * @returns Uint32Array of packed voxel data (CHUNK_SIZE^3 elements)
     */
    // ============== The column pipeline ==============
    /** One profile, reused for every column this generator resolves (perf invariant 7). */
    profile = new ColumnProfile();
    /** Rebuilt when config changes; everything column-constant the writer needs, in its own shapes. */
    writeCtx = {
        strataDepth: new Float64Array(0), strataMat: new Int32Array(0), deepMaterial: 0,
        skinDepthFine: 0, roadMaterial: 0,
    };
    refreshWriteContext() {
        const tl = this.config.terrainLayer;
        // The strata as parallel typed arrays, so the Y loop walks them inline instead of calling out
        // per voxel. The final Infinity band is the fallback and becomes `deepMaterial`.
        const bands = this.config.materialLayers.filter((l) => Number.isFinite(l.maxDepth));
        const fallback = this.config.materialLayers.find((l) => !Number.isFinite(l.maxDepth));
        this.writeCtx = {
            strataDepth: Float64Array.from(bands.map((l) => l.maxDepth)),
            strataMat: Int32Array.from(bands.map((l) => l.materialId)),
            deepMaterial: fallback ? fallback.materialId : this.config.defaultMaterial,
            skinDepthFine: tl.landformEnabled ? Math.max(1, LANDFORM_SKIN_DEPTH * this.landSizeScale()) : 0,
            roadMaterial: regionRoadMaterial(),
        };
    }
    /**
     * RESOLVE ONE COLUMN — the single place the world decides what a column contains.
     *
     * `strideFine` is the fine voxels one sample stands for (1 at LOD 0, `1 << lod` above it), and it
     * is not a detail: a coarse path samples ONE point per coarse column, so any predicate about a
     * feature thinner than the stride has to be the MAX over the fine columns that point stands for,
     * or the feature comes out DASHED. The region field does that max analytically — one evaluation
     * with a widened threshold, not (2^lod)^2 of them.
     *
     * `upto` stops after a phase. That is a real fast path, not a convenience: `sampleHeight` is the
     * hottest function in the project, and the coarse column CLIP must not pay for the skin's slope
     * sample. Phases are ordered so the two things that can be skipped are last.
     *
     * The profile is REUSED, so its fields are only valid until the next call. Every caller consumes
     * it before resolving another column.
     */
    resolveColumn(worldX, worldZ, strideFine, upto = PHASE_SKIN, lod = 0) {
        const p = this.profile;
        const tl = this.config.terrainLayer;
        p.reset(strideFine, this.sampleHeight(worldX, worldZ));
        if (upto <= PHASE_HEIGHT)
            return p;
        // --- water. The sea floods every column below the datum; its surface sits ONE fine voxel above
        // it so a full voxel of water covers the beach lip (which ducks the shoreline to sea-1),
        // otherwise the top water voxel lands exactly on the surface boundary and reads as empty.
        if (tl.landformEnabled && p.height < tl.groundLevel) {
            p.water = tl.groundLevel + 1;
            p.waterMat = tl.waterMaterial;
            p.raiseTop(p.water);
            solveWaterCrossing(p);
        }
        if (upto <= PHASE_WATER)
            return p;
        // --- fills and extrusions, in the precedence the writer applies them.
        if (this.stampDot && lod > MAX_SCATTER_LOD)
            this.addColumnDot(p, worldX, worldZ, tl);
        if (lod > MAX_BUILDING_LOD) {
            const massTop = this.cityMassTop(worldX, worldZ, p.height, strideFine);
            if (massTop > -Infinity)
                p.addExtrusion(this.cityMassDistFine, massTop, this.cityMassMat);
        }
        // THE WALL GETS THE LAST WORD. It is the one landmark the whole region system is read from, and
        // water drawn over it reads as a generation bug from 300 m away. The merge is a max, so "last"
        // cannot mean eroding what it crosses.
        const wallTop = this.regionWallTop(worldX, worldZ, p.height, strideFine);
        if (wallTop > -Infinity) {
            p.addExtrusion(this.wallDistFine, wallTop, this.regionWallMaterialAt(worldX, worldZ));
        }
        if (upto <= PHASE_EXTRUSION)
            return p;
        this.resolveSkin(p, worldX, worldZ);
        return p;
    }
    /**
     * The skin phase: material only, and therefore last — nothing downstream reads it, which is what
     * lets the clip stop before it.
     *
     * SEPARATE FROM resolveColumn SO A CALLER CAN SKIP IT PER CHUNK, and that is a measured
     * requirement rather than a tidiness. `landformSurfaceMaterial` reads the post-detail slope, which
     * is several noise samples behind a memo; LOD 0 used to pay for it only where a voxel actually
     * landed in the skin band, so a chunk of open sky or deep rock paid nothing. Hoisting it to once
     * per column — right for the coarse path, where every column is a surface column — made every
     * chunk in a vertical stack pay for every column, measured at +17% on a cold column. `skinReaches`
     * puts the laziness back without putting the per-voxel call back.
     */
    resolveSkin(p, worldX, worldZ) {
        p.surfaceMat = this.config.terrainLayer.landformEnabled
            ? this.landform.surfaceMaterial(p.height, worldX, worldZ) : 0;
        p.onRoad = this.regionRoadAt(worldX, worldZ, p.strideFine);
        // The path's paving, asked area-wise like the road (`half` is both the centre offset and the
        // cell's half-extent, isRoadCell's convention): a coarse cell is paved when the ribbon touches
        // any of it, so the paint holds whole voxels wide instead of dashing, up to the stride cap —
        // LOD 7+ rings keep the bench and drop the paint. Resolved on road columns too: the path wins.
        p.pathMat = -1;
        if (this.pathNetwork && p.strideFine <= PATH_SKIN_MAX_STRIDE) {
            const half = (p.strideFine - 1) * VOXEL_SCALE * 0.5;
            p.pathMat = this.pathNetwork.skinAt(worldX + half, worldZ + half, half);
        }
        if (p.onRoad || p.pathMat >= 0) {
            // On a slope, the face between this column and a lower neighbour is drawn from THIS
            // column's voxels down to the neighbour's height — deeper than the paving band, which
            // showed strata stripes between paved treads on steep ground. Hand the writer the drop.
            // Paved columns only (road or path), and sampleHeight is memoised, so it is near-free.
            const step = p.strideFine * VOXEL_SCALE;
            const drop = p.height - Math.min(this.sampleHeight(worldX - step, worldZ), this.sampleHeight(worldX + step, worldZ), this.sampleHeight(worldX, worldZ - step), this.sampleHeight(worldX, worldZ + step));
            if (drop > 0)
                p.pavingDropFine = drop;
        }
    }
    /**
     * Could any voxel of this chunk land in the skin band of this column?
     *
     * THE BAND REACHES ONE SAMPLE ABOVE THE SURFACE, not up to it, and the difference is a whole
     * chunk's worth of miscoloured ground. A voxel is given a material while its weight clears -0.5,
     * i.e. while `fineY < height + stride` — so the sample just above a fractional surface height has
     * depth 0 and takes the skin. Bounding this at `chunkFineY <= height` skipped the skin for every
     * chunk whose floor lands in that gap, and the ground came out as rock strata instead of grass.
     * Caught by stampCache and lodChunk, whose spreads include surface chunks where columnMemo's does
     * not.
     */
    skinReaches(p, chunkFineY) {
        const stride = p.strideFine;
        const depth = Math.max(this.writeCtx.skinDepthFine * stride, ROAD_SKIN_DEPTH, stride);
        const chunkTop = chunkFineY + (CHUNK_SIZE - 1) * stride;
        return chunkTop >= p.height - depth && chunkFineY < p.height + stride;
    }
    /**
     * The per-column tree speckle, above the level where placing each stand-in pays for itself.
     *
     * Gated on the same surface-material rule the scatter uses so the speckle appears exactly where
     * trees would, never on water, and on the same region rule — above the cutoff the dot IS the
     * trees, so a gate the scatter asks and the dot does not is a region difference that appears from
     * the air and disappears as you fly in.
     */
    addColumnDot(p, worldX, worldZ, tl) {
        if (p.water > -Infinity)
            return;
        const dot = columnDot();
        if (!dot)
            return;
        const mat = tl.landformEnabled ? this.landform.surfaceMaterial(p.height, worldX, worldZ) : 0;
        if (tl.landformEnabled && !this.landform.isStampSurfaceMaterial(mat))
            return;
        if (this.regionField && !this.regionField.allowsScatter(this.regionField.sample(worldX, worldZ).type))
            return;
        // FLOORED AT ONE COARSE VOXEL, like every other sub-stride feature: coarse rows land on
        // multiples of the stride, so a dot shorter than one has a row to be drawn in only when the
        // terrain height happens to fall the right way modulo the stride. The dot is 11.7 fine voxels,
        // so from lod 6 the speckle thins to whatever fraction of columns get lucky — 73% at lod 6,
        // 37% at lod 7 — and on ground that sits ON the lattice it disappears outright. Density has to
        // be a fact about the trees, not about the terrain's remainder.
        p.dotTop = p.height + Math.max(dot.topY, p.strideFine);
        p.dotMat = dot.material;
        p.raiseTop(p.dotTop);
    }
    /** Whether the per-column speckle is drawn at all — the stamps toggle, read once per chunk. */
    get stampDot() {
        return this.config.terrainLayer.stampsEnabled;
    }
    generateChunk(cx, cy, cz) {
        const data = new Uint32Array(CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE);
        const tl = this.config.terrainLayer;
        // No height model at all is empty air. There is nothing else that could produce ground.
        if (!tl.landformEnabled && !tl.flatEnabled)
            return data;
        const chunkWorldX = cx * CHUNK_SIZE * VOXEL_SCALE;
        const chunkWorldZ = cz * CHUNK_SIZE * VOXEL_SCALE;
        const chunkFineY = cy * CHUNK_SIZE;
        for (let lz = 0; lz < CHUNK_SIZE; lz++) {
            for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                const worldX = chunkWorldX + lx * VOXEL_SCALE;
                const worldZ = chunkWorldZ + lz * VOXEL_SCALE;
                const p = this.resolveColumn(worldX, worldZ, 1, PHASE_EXTRUSION);
                if (this.skinReaches(p, chunkFineY))
                    this.resolveSkin(p, worldX, worldZ);
                writeColumn(data, chunkFineY, lx, lz, p, this.writeCtx);
            }
        }
        // Stamps, through the node walk (docs/hierarchical-region-system.md M3a). The scatter node's
        // children ARE the placements this chunk's footprint produces, already in buildings-last order,
        // each with the fine-voxel box it writes in — so the walk prunes on the same bounds the clip
        // reads and the placer writes. Gated inside scatterPlacements on stampsEnabled.
        this.resolveNodes(data, cx, cy, cz, 0);
        return data;
    }
    // ============== LOD (coarse) generation ==============
    /**
     * Generate voxel data for a COARSE chunk at a LOD level (lod ≥ 1).
     *
     * A lod-n chunk keeps the 32³ payload but covers 2^n× the world extent per axis: each column
     * samples the terrain fields on the DECIMATED FINE LATTICE (every 2^lod-th fine voxel), so coarse
     * sample points are an exact subset of the LOD-0 lattice — the float-keyed height cache is shared
     * across LODs, and a cross-LOD seam sees coincident lattice points on the shared plane.
     *
     * Surface-only by design (far-ring representation): heights + strata/landform-skin materials +
     * sea water. Individually-placed stamps stop at MAX_SCATTER_LOD and become a per-column dot; the
     * city becomes a block silhouette above MAX_BUILDING_LOD. Both cutoffs are the kinds' own.
     *
     * Weight is measured in COARSE voxels — ((height − fineY) / 2^lod) * 0.5 — preserving the
     * ±2-voxel surface-crossing band the SurfaceNets mesher interpolates at every level. Measuring in
     * fine voxels would saturate the band within one coarse voxel and terrace the surface.
     */
    generateChunkLOD(cx, cy, cz, lod) {
        if (lod === 0)
            return this.generateChunk(cx, cy, cz);
        const data = new Uint32Array(CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE);
        const tl = this.config.terrainLayer;
        if (!tl.landformEnabled && !tl.flatEnabled)
            return data;
        const stride = 1 << lod; // fine voxels per coarse voxel
        const chunkFineY = cy * CHUNK_SIZE * stride;
        const chunkWorldX = cx * CHUNK_SIZE * stride * VOXEL_SCALE;
        const chunkWorldZ = cz * CHUNK_SIZE * stride * VOXEL_SCALE;
        const colStep = stride * VOXEL_SCALE;
        for (let lz = 0; lz < CHUNK_SIZE; lz++) {
            for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                const worldX = chunkWorldX + lx * colStep;
                const worldZ = chunkWorldZ + lz * colStep;
                const p = this.resolveColumn(worldX, worldZ, stride, PHASE_EXTRUSION, lod);
                if (this.skinReaches(p, chunkFineY))
                    this.resolveSkin(p, worldX, worldZ);
                writeColumn(data, chunkFineY, lx, lz, p, this.writeCtx);
            }
        }
        // Individually placed stand-ins, up to the level where the scatter stops paying for itself —
        // which is now the scatter node's own `expandAtLod` rather than a condition spelled out here.
        this.resolveNodes(data, cx, cy, cz, lod);
        return data;
    }
    /**
     * FILTERED coarse placements for one coarse chunk footprint, memoized per (cx,cz,lod) so every cy
     * in the column reuses it (the scatter + filter is XZ-only, exactly as at LOD 0).
     *
     * The filter is a deliberate SUBSET of the LOD-0 one FOR SCATTER, and the FULL one for BUILDINGS.
     * LOD 0 also drops stamps on pathways and over cave breaches; both warm per-cell subsystems the
     * coarse path otherwise never touches, which is the reason coarse generation is cheap (see the perf
     * guide's cost map). At ring distance a tree standing on a 4 m footpath is invisible, whereas a
     * tree floating over a river is not — so for trees the river and surface-material gates are kept
     * and the other two dropped.
     *
     * A BUILDING IS NOT A TREE HERE. A house dropped by the LOD-0 filter but drawn by the coarse one
     * exists at range and then vanishes as you walk up to it — the whole building, not a shrub — and
     * paths run through exactly the places buildings are scattered. So buildings take the LOD-0 gates
     * as well, which is affordable precisely because they are sparse: the gates are asked once per
     * PLACEMENT here (a handful per coarse chunk) rather than once per column, and isOnPathway is
     * memoized per position. That the two sets then agree at every level is asserted in
     * lodPlacementParity.test.ts.
     */
    /**
     * THE SCATTER GATE, in one place: may a stamp stand here?
     *
     * THREE QUESTIONS, ASKED IDENTICALLY AT EVERY LEVEL — and that is now a property of the code
     * rather than a thing a test has to catch. The gate list used to be level-dependent because two of
     * its members were expensive and per-tile (a breach answer warmed the cave gathers for a whole
     * tile: 113-1303 us against a pathway answer's 0.8-2.7 us), so buildings answered more questions
     * than trees and LOD 0 answered more than the coarse path. What that bought was a stamp that
     * existed at one level and not another — the pop `lodPlacementParity` exists to catch. With caves
     * and paths out of the core (docs/node-graph-refactor-plan.md C0) every survivor is cheap, so
     * every level asks the same three and parity is structural.
     *
     * ORDER IS COST, and it is measured. The gates are pure, so asking them cheapest-first cannot
     * change the answer. The region gate goes first: one warped Worley lookup, memoised per column so
     * it is free on every ask after the first, and it rejects the share of the world that is not a
     * scattering region — half of it at the default 5:3:2 weights — and everything it rejects then
     * never pays for the multi-octave surface-material sample.
     */
    keepStamp(pl, _lod) {
        const tl = this.config.terrainLayer;
        if (this.regionField) {
            const sample = this.regionField.sample(pl.worldX, pl.worldZ);
            if (!this.regionField.allowsScatter(sample.type))
                return false;
            // Nothing stands in a boundary wall. The wall is applied AFTER sampleHeight, so a stamp seated
            // on a walled column is seated on the ground and the wall is drawn straight through it.
            const footprint = getStampFootprintRadius(pl.type, pl.variant, 0) * VOXEL_SCALE;
            if (this.regionField.wallOverlaps(pl.worldX, pl.worldZ, footprint))
                return false;
        }
        // Nothing stands in the road: the ribbon plus a metre is kept clear of trees and rocks.
        if (this.pathNetwork?.onPath(pl.worldX, pl.worldZ, 1))
            return false;
        if (tl.landformEnabled && !this.landform.isStampSurface(pl.worldX, pl.worldZ))
            return false;
        return true;
    }
    lodPlacements(cx, cz, lod) {
        const key = packChunkKey(cx, cz, lod);
        let placements = this.lodPlacementCache.get(key);
        if (placements)
            return placements;
        const all = this.stampPointGenerator.generateForLodChunk(cx, cz, lod, (type) => {
            const spec = stampSpec(type);
            return !!spec && lod <= spec.maxLod;
        }, 
        // Footprint of the model ACTUALLY being placed, in fine voxels (the radius is in the stamp's
        // own units, i.e. coarse voxels at lod >= 1).
        (pl) => getStampFootprintRadius(pl.type, pl.variant, lod) * (1 << lod), 
        // THE LIST COVERS THE MESHED FOOTPRINT, NOT THE CHUNK'S OWN — two coarse columns past each
        // + face, the same ownership rule generateLodColumn scans GRID_SIZE for. A coarse chunk is
        // meshed on a GRID_SIZE³ grid, so a stand-in in the borrowed band is drawn by THIS chunk and
        // by nobody else, and stampClipMarginFine reads this list to decide the chunk EXISTS. At lod 5
        // two coarse columns are 64 fine voxels against the default 16, and the two columns whose own
        // list was empty meshed a neighbour's sunk tree below their own minCy (lodColumnSeam, claim 1).
        // The chunk DATA is unchanged: the extra admissions are stamps whose conservative footprint
        // radius does not reach this chunk, so they write nothing.
        Math.max(16, 2 * (1 << lod)));
        placements = all.filter((pl) => this.keepStamp(pl, lod));
        if (this.lodPlacementCache.size > 4096)
            this.lodPlacementCache.clear();
        this.lodPlacementCache.set(key, placements);
        return placements;
    }
    /**
     * Chunk-Y range for a coarse column footprint, in COARSE chunk indices — the vertical clip for
     * the LOD ring streamer (mirrors columnChunkRange semantics for LOD-0 tiles):
     * - maxCy: topmost coarse chunk to LOAD — from the highest content top (surface or water) plus
     *   one coarse voxel, so a flush top gets its air margin from the chunk above.
     * - minCy: coarse chunk of the voxel one BELOW the lowest surface point. Chunks strictly below
     *   it are fully solid (the coarse weight saturates one coarse voxel below the surface), so the
     *   mesher's margin policy may resolve them as SOLID without loading them. The −1 voxel is
     *   boundary OWNERSHIP (the mirror of maxCy's +1): the surface between a chunk and the chunk
     *   above is meshed only by the LOWER chunk's high face, so when the lowest surface sits in the
     *   first voxel row of a chunk, the crossing's owner is the chunk below — clipping that one away
     *   left nobody to mesh the surface (a chunk-square hole over lake basins).
     *
     * THE FOOTPRINT IS THE MESHED ONE (GRID_SIZE²), NOT THE CHUNK'S OWN CHUNK_SIZE² — the mirror of
     * the ±1-voxel ownership above, and the cause of the same-LOD coarse seams (S2-3 / RC4).
     * A coarse chunk is meshed on a GRID_SIZE³ grid: its own CHUNK_SIZE samples plus MESH_MARGIN
     * borrowed from its + X/Z neighbours (ChunkMesher.expandChunkData), and SurfaceNet gives the
     * LOW side sole ownership of
     * the shared band — the high side suppresses every face at cell 0 (SurfaceNet.ts:479-484). So the
     * surface standing in those two borrowed columns is drawn by THIS chunk and by nobody else, while
     * its HEIGHT is the neighbour's. Scanning only the chunk's own 32×32 therefore clipped away the
     * very chunk that owns the band whenever the neighbour's terrain at the shared plane rose above
     * (or fell below) everything inside this footprint: the low side never loads, the high side has
     * already suppressed its cell 0, and the seam is open forever. Measured over a 13×13 column spread
     * of the default world, 12/11/9/7/5 columns of 169 at lod 1/2/3/4/5 were short by a chunk; meshed
     * with the real mesher that showed up as open same-LOD seams (lod 4 solid 4 edges / 14.6 m, lod 5
     * solid 3 edges / 18.5 m at the block's interior planes), all of which close here.
     * Guarded by shared/src/terrain/lodColumnSeam.test.ts (cross-chunk band agreement) and, at mesh
     * level, by client/src/game/voxel/coarseSeamOwnership.test.ts. The chunk DATA is untouched — this
     * decides only WHICH chunks exist — so every generation checksum holds. Cost: 34²/32² = +12.9%
     * samples for an isolated probe, but measured at +0.0% over 108 real probes in one process
     * (best-of-5, seeds 90000+), because the two extra columns per axis ARE the next column's first
     * two and `heightCache` already holds them.
     */
    generateLodColumn(cx, cz, lod) {
        const scale = 1 << lod;
        const chunkWorldX = cx * CHUNK_SIZE * scale * VOXEL_SCALE;
        const chunkWorldZ = cz * CHUNK_SIZE * scale * VOXEL_SCALE;
        const colStep = scale * VOXEL_SCALE;
        let minH = Infinity; // lowest content (fine voxels) → solid bound
        let maxTop = -Infinity; // highest content top (fine voxels) → load bound
        // THE CLIP READS THE PROFILE THE DRAW WRITES, which is the whole reason this function stopped
        // being a third transcription of the same predicates. It used to restate the sea, the wall and
        // the city mass here, each with its own copy of the stride rule — and every one of those copies
        // was a chance to disagree with the draw about which chunks contain something. Stopping at
        // PHASE_EXTRUSION skips only the skin, which is material and cannot move a bound.
        //
        // GRID_SIZE (= CHUNK_SIZE + MESH_MARGIN) samples per axis: the chunk's own columns plus the two
        // it borrows on each + face. One constant, shared with the mesher, so the two cannot drift apart.
        for (let lz = 0; lz < GRID_SIZE; lz++) {
            for (let lx = 0; lx < GRID_SIZE; lx++) {
                const p = this.resolveColumn(chunkWorldX + lx * colStep, chunkWorldZ + lz * colStep, scale, PHASE_EXTRUSION, lod);
                if (p.bottom < minH)
                    minH = p.bottom;
                if (p.top > maxTop)
                    maxTop = p.top;
            }
        }
        if (!isFinite(minH)) {
            minH = 0;
            maxTop = 0;
        }
        // HEADROOM FOR WHAT SITS ON TOP OF THE TERRAIN (coarse stand-ins and the canopy shell), AND FOR
        // HOW FAR IT SINKS BELOW IT. maxTop above is bare terrain + water only. LOD 0 does not need this
        // — buildSurfaceColumn discovers its vertical extent by generating chunks above the surface and
        // scanning for content (LocalTerrainSource) — but the coarse clip is analytic, so anything added
        // after the column loop is invisible to it and gets sliced off at the chunk boundary. The sink
        // matters for the same reason in the other direction: a stand-in's base row lands in the surface
        // chunk's BOTTOM row, whose crossing is owned by the chunk BELOW (the mirror of maxCy's +1), and
        // leaves are transparent — so that row is a hole in the solid layer and the chunk below has a
        // real surface to mesh, an orphaned band if the clip has declared it solid rock.
        // NO MARGIN FOR THE PER-COLUMN DOT. It used to be added here, as `maxTop += dot.topY`, because
        // this loop only knew about bare terrain and water — and a margin is a guess: it was applied to
        // every column whether or not the dot's own gates (surface material, region, dry) admitted one.
        // The dot is on the profile now, on the columns that actually carry it, so adding it again here
        // would raise the clip above anything drawn.
        // ...and the node walk's own extent: scatter stand-ins to MAX_SCATTER_LOD, city towers to
        // MAX_BUILDING_LOD. Asked UNCONDITIONALLY rather than re-stating either ladder here — each kind
        // prunes itself out above its own cutoff, and the resolver stops before it costs anything.
        const span = this.nodeClipSpanFine(cx, cz, lod);
        if (span) {
            maxTop = Math.max(maxTop, span.top);
            minH = Math.min(minH, span.bottom);
        }
        const maxCy = Math.floor((Math.floor(maxTop / scale) + 1) / CHUNK_SIZE);
        // TWO voxels below the lowest surface sample, not one. The coarse weight saturates one coarse
        // voxel below the surface, so the lowest CROSSING is the cell between rows r-1 and r where
        // r = floor(minH / scale) — and a cell at grid index 0 emits no faces (SurfaceNet's
        // `onLowBoundary`), so when r-1 happens to be a chunk's FIRST row that crossing is faced only by
        // the chunk BELOW, through its own cell 32. One voxel of margin loads the chunk holding r-1;
        // that is the wrong one in exactly the 1-in-32 columns where r-1 is a first row, and it is the
        // whole of it, because the chunk below then draws a band nobody loaded. Masked until now by the
        // unconditional stamp sink this clip used to subtract (lodColumnSeam claim 1, lod 5, seed 12345).
        const minCy = Math.floor((Math.floor(minH / scale) - 2) / CHUNK_SIZE);
        return { minCy, maxCy };
    }
    /**
     * The absolute fine-voxel span everything the NODE WALK puts in this column occupies, or null.
     *
     * ASKED OF EACH STAMP WHERE IT ACTUALLY SITS, not as a margin on the column's terrain — and that is
     * the fix rather than the style. A margin has to be added to something, and the only candidate is the
     * max terrain height over the column's own 34 samples; but the placement list deliberately reaches TWO
     * COARSE COLUMNS PAST that footprint (a stand-in in the borrowed band is drawn by this chunk and by
     * nobody else), and a stamp out there is seated on terrain this column never sampled. When that
     * terrain is higher, the stamp's top clears the clip and the chunk that owns its band is never loaded:
     * measured at lod 5 as 25.0 m of open same-LOD solid seam at a chunk plane (coarseSeamOwnership),
     * which appeared the moment the seat stopped truncating downward and started rounding.
     *
     * Both halves use `stampSeatRow`, the placer's own arithmetic, so the clip and the draw cannot
     * disagree about where a stamp is. Cheap where it is used: `lodPlacements` is memoized per (cx,cz,lod)
     * and `getStamp` per model, and the bundle path generates this column's chunks in the same instance
     * right after asking for the clip — both are cache hits it was going to take anyway.
     */
    nodeClipSpanFine(cx, cz, lod) {
        // No stamp-system guard: the scatter kind's own placements are empty without one, and the city
        // kinds do not use it at all — a guard here would silently clip towers out of a stampless world.
        // ASKED OF THE NODE WALK, so the clip and the draw read one set of bounds instead of two copies
        // of the same arithmetic. Y IS UNBOUNDED here and that is the point: a column's clip is computed
        // before any chunk in it exists, so there is no Y window to prune against — only the footprint.
        const span = CHUNK_SIZE * (1 << lod);
        const margin = Math.max(16, 2 * (1 << lod));
        const sink = {
            cx, cy: 0, cz, lod, data: EMPTY_SINK_DATA,
            window: {
                minX: cx * span - margin, maxX: (cx + 1) * span + margin,
                minY: -Infinity, maxY: Infinity,
                minZ: cz * span - margin, maxZ: (cz + 1) * span + margin,
            },
        };
        return this.nodes.span([scatterRoot(sink), ...cityRoots(sink, this.cityDeps)], sink, this.nodeContext());
    }
}
//# sourceMappingURL=TerrainGenerator.js.map